import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { STORY_IMAGES } from "../../lib/content";

export default function Hero() {
  const handleScroll = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-cream pt-28 md:pt-32 pb-20"
    >
      {/* Right floating bowl image (absolute so the title can overflow over it) */}
      <div className="absolute right-4 md:right-12 lg:right-20 top-[18%] md:top-[22%] z-10 w-[42%] sm:w-[34%] md:w-[28%] lg:w-[24%] max-w-[420px] aspect-[3/4] overflow-hidden glow-border rounded-sm">
        <img
          src={STORY_IMAGES.hero}
          alt="Crafted matcha bowl"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 right-3 md:right-4 flex items-end justify-between">
          <span className="overline text-bone text-[0.55rem] md:text-[0.7rem]">
            N°01
          </span>
          <span className="font-cormorant italic text-bone text-xs md:text-sm">
            Hand-whisked
          </span>
        </div>
      </div>

      {/* Title — oversized, breaks the grid, overlaps the image */}
      <div className="relative z-20 mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 pt-12 md:pt-20 pointer-events-none">
        <motion.h1
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display leading-[0.86] text-ink select-none"
          style={{
            fontSize: "clamp(5rem, 21vw, 22rem)",
            letterSpacing: "0.02em",
            mixBlendMode: "multiply",
          }}
        >
          MATCHA
        </motion.h1>
        <motion.h1
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.1, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="font-display leading-[0.86] text-matcha-dark/80 select-none -mt-2 md:-mt-4"
          style={{
            fontSize: "clamp(5rem, 21vw, 22rem)",
            letterSpacing: "0.02em",
            mixBlendMode: "multiply",
          }}
        >
          STORY
        </motion.h1>
      </div>

      {/* Bottom row — meta + tagline + CTAs (plain divs to avoid stuck framer opacity) */}
      <div className="relative z-30 mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 mt-10 md:mt-16 grid grid-cols-12 gap-6 md:gap-12 items-end">
        <div className="col-span-12 md:col-span-4">
          <div className="space-y-4 max-w-sm">
            <p className="overline text-matcha-dark">Est. 2024 · Vol. I</p>
            <p className="font-cormorant italic text-2xl md:text-3xl text-ink/85 leading-snug">
              The leaf, the bowl, the pause.
            </p>
            <p className="text-sm text-ink-soft leading-relaxed">
              Single-origin ceremonial matcha, stone-ground in small batches and
              whisked the moment you order — nothing in between.
            </p>
          </div>
        </div>

        <div className="col-span-12 md:col-span-5 md:col-start-5">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-10 md:w-16 bg-ink/35" />
            <span className="overline text-ink-soft whitespace-nowrap text-[0.7rem] md:text-xs">
              Crafted Matcha · Desserts · Good Vibes
            </span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button
              data-testid="hero-cta-menu"
              onClick={() => handleScroll("#menu")}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-ink text-bone hover:bg-matcha-dark transition-all duration-300 text-xs uppercase tracking-[0.22em] font-medium"
            >
              Read the Menu
            </button>
            <button
              data-testid="hero-cta-story"
              onClick={() => handleScroll("#story")}
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-medium text-ink-soft hover:text-matcha-dark transition-colors"
            >
              Our Story →
            </button>
          </div>
        </div>

        <div className="hidden md:block col-span-3 col-start-10 text-right">
          <p className="overline text-ink-soft mb-2">A Matcha House</p>
          <p className="font-cormorant italic text-ink/70 text-base">
            Kuala Lumpur · Est. 2024
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        data-testid="hero-scroll-indicator"
        onClick={() => handleScroll("#story")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 text-ink-soft hover:text-matcha-dark transition-colors group"
      >
        <span className="overline text-[0.65rem]">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowDown size={16} />
        </motion.span>
      </button>
    </section>
  );
}
